import { useEffect } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import SvgIcon, { SvgIconProps } from "@mui/material/SvgIcon";
import FacebookIcon from "@mui/icons-material/Facebook";
import YouTubeIcon from "@mui/icons-material/YouTube";
import PinterestIcon from "@mui/icons-material/Pinterest";
import BlogCarousel from "../components/blogs_carousel";
import WorldMap from "../components/locations";
import MomentsCarousel from "../components/moments_carousel";
import { blogPosts } from "../data/blogPosts";
import { IconButton } from "@mui/material";

const ASSET_BASE = process.env.PUBLIC_URL || "";

const InstagramIcon = (props: SvgIconProps) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
  </SvgIcon>
);

interface BrandCard {
  name: string;
  description: string;
  logo?: string;
  bgImage: string;
  showIcons?: boolean;
  links?: Record<string, string>;
  textColor: string;
  footer?: string;
  link: string;
}

const brandCards: BrandCard[] = [
  {
    name: "Kmart",
    description:
      "Established in 1969, Kmart operates more than 309 stores throughout Australia and New Zealand, offering customers a wide range of apparel and general merchandise products at low prices, every day.",
    logo: `${ASSET_BASE}/assets/kmartlogo.png`,
    bgImage: `${ASSET_BASE}/assets/cardbg1.png`,
    showIcons: true,
    links: {
      facebook: "https://www.facebook.com/KmartAustralia",
      instagram: "https://www.instagram.com/kmartaus/",
      youtube: "https://www.youtube.com/user/kmartaustralia",
    },
    textColor: "#ffffff",
    link: "https://www.kmart.com.au/",
  },
  {
    name: "Target",
    description:
      "Target began as a drapery store in 1926 and has since grown to become a national apparel and general merchandise retailer with 130 stores across Australia. Its objective is to provide quality and style at affordable prices.",
    logo: `${ASSET_BASE}/assets/New-Target.png`,
    bgImage: `${ASSET_BASE}/assets/cardbg2.png`,
    showIcons: true,
    links: {
      facebook: "https://www.facebook.com/targetaus",
      instagram: "https://www.instagram.com/targetaus/",
      youtube: "https://www.youtube.com/user/TargetAustralia",
      pinterest: "https://au.pinterest.com/targetaus/",
    },
    textColor: "#000000",
    link: "https://www.target.com.au/",
  },
  {
    name: "Anko Global",
    footer:
      "Anko Global is an innovative product development company dedicated to enhancing everyday living with stylish, affordable products. Representing over 85% of Kmart Australia’s offerings, Anko Global combines quality and value, making it a trusted choice for consumers. With a commitment to ethical sourcing and sustainable practices, Anko Global continues to evolve, ensuring a diverse range of on-trend products that meet the needs of customers worldwide.",
    bgImage: `${ASSET_BASE}/assets/cardbg3.png`,
    textColor: "#ffffff",
    description:
      "Expanding Kmart's operational capabilities across global markets and borders.",
    link: "https://ankoglobal.com/",
  },
  {
    name: "Anko GCC",
    footer:
      "Anko GCC plays a crucial role in supporting Kmart and Target’s commitment to innovation and operational excellence. The center focuses on developing new capabilities, advancing technology, and nurturing a culture of continuous improvement. By driving ongoing improvements in the retail sector, Anko GCC empowers its team members to grow in their careers, enhance their skills, and actively contribute to shaping the future of retail.",
    bgImage: `${ASSET_BASE}/assets/cardbg4.png`,
    textColor: "#ffffff",
    description:
      "Anko Sourcing's Innovation Hub for Kmart's IT, Analytics, and Finance",
    link: "https://ankogcc.com/",
  },
];

const iconComponents: Record<string, React.ComponentType<SvgIconProps>> = {
  facebook: FacebookIcon,
  instagram: InstagramIcon,
  youtube: YouTubeIcon,
  pinterest: PinterestIcon,
};

function SocialIcons({
  links,
  color,
}: {
  links: Record<string, string>;
  color: string;
}) {
  return (
    <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
      {Object.entries(links).map(([key, url]) => {
        const Icon = iconComponents[key];
        if (!Icon) return null;
        return (
          <IconButton
            key={key}
            component="a"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: color,
              border: `1.5px solid ${color}`,
              p: "6px",
              transition: "all 0.3s ease",
              "&:hover": {
                bgcolor: "#b7127e",
                borderColor: "#b7127e",
                color: "#ffffff",
              },
            }}
          >
            <Icon sx={{ fontSize: { xs: 18, md: 20 } }} />
          </IconButton>
        );
      })}
    </Box>
  );
}
const SECTION_PADDING = {
  xs: "40px 24px",
  sm: "48px 32px",
  md: "64px 64px",
  lg: "80px 80px",
};

function Home() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  return (
    <Box component="main">
      <Box
        component="section"
        sx={{
          position: "relative",
          width: "100%",
          height: { xs: "55vh", sm: "65vh", md: "75vh" },
          maxHeight: { md: "600px", lg: "700px" },
          minHeight: "500px",
          mb: { xs: 8, md: 14 },

          overflow: "hidden",
        }}
      >
        <Box
          component="video"
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          sx={{ width: "100%", height: "100%", objectFit: "cover" }}
        >
          <source
            src={`${ASSET_BASE}/assets/Anko_Banner_1440x600_Magenta.gif.mp4`}
            type="video/mp4"
          />
        </Box>

        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: { xs: "24px", sm: "40px", md: "64px", lg: "80px" },
            transform: "translateY(-50%)",
            maxWidth: { xs: "calc(100% - 48px)", sm: 420, md: 560, lg: 680 },
          }}
        >
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: "2.5rem", md: "3.5rem", lg: "4rem" },
              fontWeight: 700,
              lineHeight: 1.2,
              animation: "heroFadeUp 0.9s ease both",
              "@keyframes heroFadeUp": {
                from: { opacity: 0, transform: "translateY(36px)" },
                to: { opacity: 1, transform: "translateY(0)" },
              },
            }}
          >
            Sourcing from the
            <br />
            best for
            <br />
            unbeatable value
          </Typography>
        </Box>
      </Box>

      <Box
        component="section"
        sx={{
          mt: { xs: 6, md: 10, lg: 12 },
          px: { xs: 3, sm: 5, md: 8, lg: 12 },
          mb: { xs: 1, md: 1 },
        }}
      >
        <Box sx={{ maxWidth: 980, mx: "auto" }}>
          <Typography
            variant="h2"
            sx={{
              textAlign: "left",
              mb: { xs: 3, md: 4 },
            }}
          >
            We Are a Team With a Purpose
          </Typography>

          <Typography
            variant="body1"
            sx={{
              textAlign: "left",
              color: "text.primary",
            }}
          >
            We are the direct sourcing arm for the iconic Australian retail
            brands, Kmart and Target. As a team of highly experienced retail
            sourcing professionals, we thrive to bring globally compelling low
            cost, on-trend products to our customers in Australia and New
            Zealand. We follow the highest standards to source ethically and
            operate with a clear focus on the welfare of our planet and our
            people.
          </Typography>
        </Box>
      </Box>

      {/* --- Statistics Section --- */}
      <Box
        component="section"
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "center",
          justifyContent: "center",
          gap: { xs: 5, md: 10, lg: 16 },
          pt: { xs: 2, md: 3 }, // Reduced top padding to bring it closer to the text above
          pb: { xs: 5, md: 8, lg: 10 },
          px: { xs: 4, sm: 6, md: 10, lg: 14 },
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <Typography
            sx={{
              // Switched font to bold and weight to 700
              fontFamily: "'Ankomoderat-bold', Helvetica, Arial, sans-serif",
              color: "secondary.main",
              fontSize: { xs: "2.5rem", sm: "3rem", md: "3.5rem", lg: "4rem" },
              fontWeight: 700,
              lineHeight: 1,
              mb: 1,
            }}
          >
            85%
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              color: "#1a1a1a", // Darker black/grey as requested
              textAlign: "center",
              fontWeight: 500, // Medium weight for "darker" look
              lineHeight: 1.2,
            }}
          >
            Australian shoppers shop with
            <br />
            us every year
          </Typography>
        </Box>

        <Divider
          orientation="vertical"
          flexItem
          sx={{
            display: { xs: "none", md: "block" },
            height: 100,
            alignSelf: "center",
            borderRightWidth: 2.5, // Bolded the dividing line
            borderColor: "rgba(0, 0, 0, 0.15)", // Slightly more visible grey
          }}
        />

        <Box sx={{ textAlign: "center" }}>
          <Typography
            sx={{
              fontFamily: "'Ankomoderat-bold', Helvetica, Arial, sans-serif",
              color: "primary.main",
              fontSize: { xs: "2.5rem", sm: "3rem", md: "3.5rem", lg: "4rem" },
              fontWeight: 700,
              lineHeight: 1,
              mb: 1,
            }}
          >
            600+
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              color: "#1a1a1a",
              textAlign: "center",
              fontWeight: 500,
              lineHeight: 1.2,
            }}
          >
            Empowered team
            <br />
            members make it possible
          </Typography>
        </Box>
      </Box>

      <Box
        component="section"
        sx={{
          paddingY: { md: 3 },
          width: "100%",
          height: { xs: "auto" },
          backgroundImage: {
            xs: "none",
            md: `url(${ASSET_BASE}/assets/bg-image.png)`,
          },
          backgroundSize: "cover",
          backgroundPosition: "left center",
          backgroundRepeat: "no-repeat",
          bgcolor: "#1a1a1a",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "stretch",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            flex: "0 0 50%",
            minWidth: "50%",
            display: { xs: "none", md: "block" },
          }}
        />

        <Box
          sx={{
            flex: "0 0 50%",
            maxWidth: { xs: "100%", md: "50%" },
            bgcolor: { xs: "#1a1a1a", md: "transparent" },
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignContent: "end",
            p: { xs: "40px 24px", md: "56px 40px", lg: "50px 40px" },
            color: "common.white",
            zIndex: 2,
          }}
        >
          <Typography
            variant="h3"
            sx={{
              color: "common.white",
              fontSize: {
                xs: "2.2rem",
                sm: "2.8rem",
                md: "clamp(2.2rem, 3.5vw, 4rem)",
              },
              mb: { xs: 2.5, md: 5 },
              letterSpacing: "0.5px",
            }}
          >
            Driven by Values
          </Typography>

          <Typography
            variant="body2"
            sx={{
              opacity: 0.92,
              fontWeight: 500,
              fontSize: { xs: "1.1rem", md: "clamp(1rem, 1.2vw, 1.2rem)" },
              fontStyle: "bold",
            }}
          >
            At Anko Sourcing, we continuously strive to create a values-driven
            work culture that recognizes and rewards innovation, collaboration,
            learning, and high performance. Through purpose-driven partnerships,
            we amplify our capabilities and expand our reach, fostering an
            ecosystem where shared goals lead to collective success. As a brand
            that celebrates every individual, we cultivate profound customer
            love, ensuring that our commitment to excellence enhances the
            experience for every customer we serve.
          </Typography>
        </Box>

        <Box
          sx={{
            display: { xs: "block", md: "none" },
            width: "100%",
            height: "350px",
            backgroundImage: `url(${ASSET_BASE}/assets/homepage.png)`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
      </Box>

      <Box
        component="section"
        sx={{
          display: "flex",
          alignItems: "center",
          p: {
            xs: "48px 24px",
            sm: "64px 40px",
            md: "96px 64px",
            lg: "112px 80px",
          },
          backgroundImage: `url(${ASSET_BASE}/assets/bg_image.png)`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {/* Text constrained to 60% of page width */}
        <Box sx={{ flex: { xs: "0 0 100%", md: "0 0 60%" }, maxWidth: { xs: "100%", md: "60%" } }}>
          <Typography
            variant="h3"
            sx={{
              color: "text.secondary",
              mb: 2.5,
              fontWeight: 700,
              lineHeight: 1.15,
              textAlign: "left",
              /* Keep your whiteSpace logic */
              whiteSpace: { md: "nowrap", xs: "normal" },

              /* 1. RETAIN MAC SIZES */
              fontSize: {
                xs: "1.8rem",
                sm: "2.5rem",
                md: "clamp(2rem, 2.8vw, 2.8rem)",
                lg: "3.2rem",
              },

              "@media (min-width:1200px) and (max-width:1550px)": {
                fontSize: "2.7rem",
                whiteSpace: "normal", 
              },
            }}
          >
            Building a Sustainable Future,{" "}
            <Box
              component="span"
              sx={{ display: { xs: "block", md: "block" } }}
            >
              Together
            </Box>
          </Typography>

          <Typography
            variant="body1"
            sx={{
              fontFamily: "'Ankomoderat-regular', Helvetica, Arial, sans-serif",
              color: "text.secondary",
              lineHeight: "1.6em",
              fontSize: { xs: "1.1rem", md: "1.1rem", lg: "1.2rem" },
              textAlign: "justify",
            }}
          >
            As a large retailer, we know we have a responsibility to care for
            and respect our planet and all the lives that we touch. We take this
            responsibility very seriously and have an opportunity to use our
            relationships and scale to make a difference. This is where our
            Better Together sustainable development program comes in; the Kmart
            Group's integrated sustainability plan and a core element of our
            business strategy.
          </Typography>
        </Box>

        {/* We keep this but allow it to shrink more so the text box can be wider */}
        <Box
          sx={{
            flex: { md: 0.2, lg: 1 },
            display: { xs: "none", md: "block" },
          }}
        />
      </Box>

      <Box
        component="section"
        sx={{
          p: SECTION_PADDING,
          bgcolor: "background.default",
        }}
      >
        <Typography variant="h2" sx={{ textAlign: "left", mb: 4 }}>
          Our Locations
        </Typography>
        <Box sx={{ maxWidth: 1400, mx: "auto" }}>
          <WorldMap />
        </Box>
      </Box>

      <Box
        component="section"
        sx={{
          p: SECTION_PADDING,
          bgcolor: "background.default",
        }}
      >
        <Typography
          variant="h2"
          sx={{
            textAlign: "left",
            mb: { xs: 4, md: 6 },
          }}
        >
          Our Global Presence
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "repeat(2, 1fr)" },
            gap: { xs: 3, md: "40px" },
            width: "100%",
          }}
        >
          {brandCards.map((card) => {
            const isTarget = card.name.toLowerCase().includes("target");

            return (
              <Box
                component="article"
                key={card.name}
                sx={{
                  borderRadius: "32px",
                  p: { xs: "32px 24px", md: "40px" },
                  minHeight: { xs: 350, md: 500 },
                  display: "flex",
                  flexDirection: "column",
                  backgroundImage: `url(${card.bgImage})`,
                  backgroundSize: "cover",
                  backgroundPosition: "bottom right",
                  backgroundRepeat: "no-repeat",
                  color: card.textColor,
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    mb: 4,
                    minHeight: 44,
                  }}
                >
                  {card.logo ? (
                    <Box
                      component="img"
                      src={card.logo}
                      alt={card.name}
                      sx={{ height: { xs: 32, md: 44 }, width: "auto" }}
                    />
                  ) : (
                    <Typography
                      variant="h4"
                      sx={{
                        color: card.textColor,
                        fontWeight: 700,
                        fontSize: { xs: "1.8rem", md: "2rem", lg: "2.25rem" },
                        lineHeight: 1.15,
                      }}
                    >
                      {card.name}
                    </Typography>
                  )}

                  {card.showIcons && card.links && (
                    <SocialIcons links={card.links} color={card.textColor} />
                  )}
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: card.logo
                        ? "1.1rem"
                        : { xs: "1.2rem", md: "1.35rem" },
                      mb: 2,
                      lineHeight: 1.5,
                      fontWeight: card.logo ? 400 : 500,
                    }}
                  >
                    {card.description}
                  </Typography>

                  {card.footer && (
                    <Typography
                      sx={{
                        fontSize: "1rem",
                        opacity: 0.85,
                        mb: 3,
                        lineHeight: 1.5,
                      }}
                    >
                      {card.footer}
                    </Typography>
                  )}

                  <Button
                    variant="outlined"
                    href={card.link}
                    target="_blank"
                    sx={{
                      borderRadius: "24px 24px 0px 24px",
                      textTransform: "none",
                      px: 3.5,
                      py: 0.8,
                      fontWeight: 600,
                      borderColor: isTarget ? "#000000" : "#ffffff",
                      color: isTarget ? "#000000" : "#ffffff",
                      borderWidth: "1.5px",
                      "&:hover": {
                        borderWidth: "1.5px",
                        bgcolor: "#ffffff",
                        color: "#b7127e",
                        borderColor: "#ffffff",
                      },
                    }}
                  >
                    Know More
                  </Button>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>

      <Box component="section" sx={{ width: "100%" }}>
        <MomentsCarousel />
      </Box>

      <Box
        component="section"
        sx={{
          width: "100%",
          p: SECTION_PADDING,
          bgcolor: "background.default",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
            mb: { xs: 4, md: 6 },
          }}
        >
          <Typography variant="h2" sx={{ textAlign: "left" }}>
            Blogs
          </Typography>
        </Box>

        <BlogCarousel blogPosts={blogPosts} />
      </Box>
    </Box>
  );
}

export default Home;
